/**
 * Copyright(c) 2024 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module Analytics
 *
 * @author Naseef O
 */

import { Money } from "../api/saleor/generated/graphql";
import {
  AttributesTypes,
  Checkout,
  CheckoutLines,
  GaPageInfo,
  GAUserObject,
  LCSkuType,
  MediaItem,
  MyAccountOrder,
  Order,
  OrderLines,
  Price,
  Product,
  PromotionalBannerItem,
  TypeSessionUser,
  SaleorMetadata,
  VariantItem,
  VariantItemPdp,
  WishlistVariant,
} from "../common/lib/types";
import { GoogleTagManager as Gtm, sendGTMEvent } from "@next/third-parties/google";
import { AlgoliaProductItem, RecommendProductItem } from "./algoliaUtils/types";
import { createGaInitialObject, generateHash, hasArrayElements, toFixed } from "./commonUtils";
import {
  analyticEventTypes,
  EyeTestEventActions,
  paymentConstants,
  promotionType,
  SaleorAttributes,
} from "./constants";
import {
  getAttributeValue,
  getCalculatedPriceInfo,
  getPriceForBuyXgetY,
  getVariantImages,
} from "./productUtils";
import { extractValueBySlug, getProductImages } from "@/utils/product.utils";

interface AnalyticsItemType {
  item_id: string;
  item_name: string;
  coupon: string;
  discount: number;
  index: number;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_list_id: string;
  item_list_name: string;
  item_variant: string;
  price: number;
  quantity: number;
  sale_status: string;
  stock_status: string;
  discount_percent: string;
  discount_name?: string;
  product_gender: string;
  product_image?: string;
}

interface AnalyticsCartItemType extends AnalyticsItemType {
  buy_flow?: string;
}

interface PromotionItem {
  promotion_id: string;
  promotion_name: string;
  creative_name: string;
  creative_slot: string;
  location_id: string;
}

interface PromotionEventType {
  items: PromotionItem[];
}

interface ViewItemListEventType {
  currency: string;
  item_list_id: string;
  item_list_name: string;
  items: AnalyticsItemType[];
}

interface SelectItemEventType {
  currency: string;
  item_list_id: string;
  item_list_name: string;
  items: AnalyticsItemType[];
}

interface ViewItemEventType {
  currency: string;
  value: number;
  item_list_name: string;
  item_list_id: string;
  items: AnalyticsItemType[];
}

interface AddToWishListEventType {
  currency: string;
  value: number;
  item_list_name: string;
  item_list_id: string;
  items: AnalyticsItemType[];
}

interface AddToCartEventType {
  currency: string;
  value: number;
  item_list_name: string;
  item_list_id: string;
  items: AnalyticsCartItemType[];
}

interface RemoveFromCartEventType {
  currency: string;
  value: number;
  item_list_name: string;
  item_list_id: string;
  items: AnalyticsCartItemType[];
}

interface ViewCartEventType {
  currency: string;
  value: number;
  item_list_id: string;
  item_list_name: string;
  items: AnalyticsCartItemType[];
}

interface BeginCheckoutEventType extends ViewCartEventType {
  coupon: string;
  Gift_added: string;
  Gift_price: number;
}

interface ShippingInfoEventType extends BeginCheckoutEventType {
  shipping_country: string;
  shipping_city: string;
  shipping_tier: string;
  shipping_method: string;
  shipping_area: string;
  shipping_address: string;
}

interface PaymentInfoEventType extends BeginCheckoutEventType {
  payment_type: string;
}

interface PurchaseEventType extends ShippingInfoEventType {
  transaction_id: string;
  tax: number;
  shipping: number;
  payment_method: string;
}

interface RefundEventType {
  currency: string;
  transaction_id: string;
  value: number;
  coupon: string;
  shipping: number;
  tax: number;
  items: AnalyticsCartItemType[];
}

type CartItemsEvent = {
  event: string;
  CartProducts: string;
  productInCart: number;
  ecommerce: { currency: string; value: number; items: AnalyticsCartItemType[] };
};

type EventType = {
  event: string;
  ecommerce?:
  | PromotionEventType
  | ViewItemListEventType
  | SelectItemEventType
  | ViewItemEventType
  | AddToWishListEventType
  | AddToCartEventType
  | RemoveFromCartEventType
  | ViewCartEventType
  | BeginCheckoutEventType
  | ShippingInfoEventType
  | PaymentInfoEventType
  | PurchaseEventType
  | RefundEventType;

  method?: string;
  CouponCode?: string;
  Error?: string;
  Notes?: string;
  price?: number;
  userEmail?: string;
  purchaseUserEmail?: string;
  purchaseUserFirstName?: string;
  purchaseUserLastName?: string;
  purchaseUserPhoneNumber?: string;
  purchaseUserAddress?: string;
  purchaseCustomerId?: string;
  hashedEmail?: string;
  order_ID?: string;
  eventAction?: string;
  eventLabel?: string;
  TestType?: string;
  StoreName?: string;
  DateTime?: string;
  message?: string;
  formId?: string;
  formType?: string;
};

interface UserEventType extends GAUserObject {
  event: string;
}

interface CommonProductProperties {
  hitIndex?: number;
  selectedQuantity?: number;
  addOnSkus?: LCSkuType;
}

interface ExtendedRecommendProductItem extends RecommendProductItem, CommonProductProperties { }
interface ExtendedAlgoliaProductItem extends AlgoliaProductItem, CommonProductProperties { }
interface ExtendedProduct extends Product, CommonProductProperties { }

interface ExtendedCheckoutLines extends CheckoutLines {
  selectedQuantity?: number;
}

interface ExtendedVariantItem extends WishlistVariant {
  selectedQuantity?: number;
}

type PostAnalyticsDataType = {
  target?: string;
  source?: string;
  sourceId?: string;
  currency?: string;
  message?: string;
  product?: ExtendedRecommendProductItem | ExtendedAlgoliaProductItem | ExtendedProduct;
  selectedVariantId?: string;
  buyFlow?: string;
  cart?: Checkout;
  order?: MyAccountOrder;
  saleorOrder?: Order;
  lineItem?: ExtendedCheckoutLines;
  products?: AlgoliaProductItem[];
  variant?: ExtendedVariantItem;
  promotions?: PromotionalBannerItem[];
  method?: string;
  userData?: GAUserObject;
  paymentType?: string;
  orderId?: string;
  gaPageInfo?: GaPageInfo;
  locale?: string;
  user?: TypeSessionUser;
  magrabiID?: string;
  couponData?: {
    couponCode?: string;
    error?: string;
  };
  giftData?: {
    notes?: "Yes" | "No";
    price?: number;
  };
  formData?: {
    formId?: string;
    formType?: string;
  };
  email?: string;
  bookEyeTestData?: {
    eventAction?: EyeTestEventActions;
    eventLabel?: string;
    testType?: string;
    storeName?: string;
    dateTime?: string;
  };
  isQuantityIncrement?: boolean;
};

type PostAnalyticsType = {
  eventType: string;
  data: PostAnalyticsDataType;
};

type Pricing = { net: number; gross: number };

interface AnalyticsDataType extends AnalyticsCartItemType {
  currency?: string;
  value: number;
}

interface CartDetails {
  value: number;
  currency: string;
  shippingCountry: string;
  shippingCity: string;
  shippingTier: string;
  shippingMethod: string;
  shippingArea: string;
  shippingAddress: string;
  coupon: string;
  giftAdded: string;
  giftPrice: number;
  numberOfItems: number;
  tax: number;
  shipping: number;
  paymentType?: string;
}

interface OrderDetails {
  currency: string;
  value: number;
  coupon: string;
  shipping: number;
  tax: number;
  orderId: string;
}

export const analyticEvents = {
  PRODUCT_CLICK: "select_item",
  VIEW_PROMOTION: "view_promotion",
  SELECT_PROMOTION: "select_promotion",
  VIEW_ITEM_LIST: "view_item_list",
  VIEW_ITEM: "view_item",
  ADD_TO_WISHLIST: "add_to_wishlist",
  ADD_TO_CART: "add_to_cart",
  REMOVE_FROM_CART: "remove_from_cart",
  VIEW_CART: "view_cart",
  BEGIN_CHECKOUT: "begin_checkout",
  ADD_SHIPPING_INFO: "add_shipping_info",
  ADD_PAYMENT_INFO: "add_payment_info",
  PURCHASE: "purchase",
  REFUND: "refund",
  CART_ITEMS: "cart-items",
  LOGIN: "login",
  SIGN_UP: "sign_up",
  TRACK_ORDER: "track_order",
  BOOK_EYE_TEST: "book_eye_test",
  BOOK_EYE_TEST_SUCCESS: "book_eye_test_success",
  COUPON_CODE_VALID: "couponCode_valid",
  COUPON_CODE_INVALID: "couponCode_Invalid",
  GIFT_ADDED: "Gift_Added",
  NEWSLETTER_SUBSCRIPTION: "newsletter_subscription",
  CHECKOUT_ERROR: "checkout_error",
  CUSTOM_PAGE_VIEW: "custom_page_view",
  QUANTITY_ADJUSTMENT: "QUANTITY_ADJUSTMENT",
  FORM_SUBMISSION: "form_submission",
};

const paymentTypes = {
  GPAY: "GPAY",
  CARD: "CARD",
  PAYMOB: "PAYMOB",
};

export const GoogleTagManager = Gtm;

/**
 * This function is used to handle Analytic events
 *
 * @param {EventType} data
 */
const handleAnalyticEvent = (data: EventType | CartItemsEvent | UserEventType): void => {
  // Clears ecommerce to ensure previous data is not pushed to GTM
  // sendGTMEvent({ ecommerce: null });

  sendGTMEvent(data);
};

const parsePrice = (price?: Price) => Number(price?.gross?.amount) || undefined;

const parseAlgoliaPrice = (price: Pricing) => Number(price?.gross) || undefined;

const parseCartPrice = (price?: Money) => Number(price?.amount) || undefined;

const calculateDiscount = (price: number, discountedPrice: number) =>
  price > 0 && discountedPrice > 0 ? price - discountedPrice : 0;

const calculateDiscountPercent = (price: number, discountedPrice: number) =>
  price > 0 && discountedPrice > 0
    ? `${toFixed(((price - discountedPrice) / Math.abs(price)) * 100, 2)}%`
    : "0%";

const getQuantity = (quantity?: number) => Number(quantity) || undefined;

const getOfferStatus = (hasDiscount: boolean) => (hasDiscount ? "On Offer" : "No offer");

const getGender = (attributes?: AttributesTypes[] | Record<string, string>): string => {
  if (Array.isArray(attributes)) {
    return (getAttributeValue(attributes, SaleorAttributes.GENDER) ||
      getAttributeValue(attributes, SaleorAttributes.ACC_GENDER) ||
      "") as string;
  }

  return (attributes?.[SaleorAttributes.GENDER] || attributes?.["gender"]) ?? "";
};

const getMergedLines = (lines: CheckoutLines[]) => {
  const addOns: CheckoutLines[] = [];
  const products: Record<string, CheckoutLines> = {};

  lines.forEach((line) => {
    if (line?.parent?.id) {
      addOns.push(line);
    } else {
      products[line.id] = { ...line, addOns: [] };
    }
  });

  addOns.forEach((addOn) => {
    if (products[addOn.parent.id]) {
      products[addOn.parent.id].addOns?.push(addOn);
    }
  });

  return Object.values(products);
};

const getUpdatedLineItems = (lines?: OrderLines) => {
  if (!lines) return lines;

  const updatedLines = [...lines];

  lines.forEach((lineItem: any) => {
    lineItem.addOns?.forEach((addOnItem: any) => {
      const index = updatedLines.findIndex((item) => item?.id === addOnItem?.id);
      if (index !== -1) {
        updatedLines.splice(index, 1);
      }
    });
  });

  return updatedLines;
};

const calculateLensPrice = (lineItem: CheckoutLines) => {
  return {
    price:
      lineItem?.addOns?.reduce((acc, item) => {
        return acc + (item?.totalPrice?.gross?.amount ?? 0);
      }, 0) ?? 0,
    undiscountedPrice:
      lineItem?.addOns?.reduce((acc, item) => {
        return acc + (item?.variant?.pricing?.priceUndiscounted?.gross?.amount ?? 0);
      }, 0) ?? 0,
  };
};

const getCartLineItems = ({
  cart,
  source,
  buyFlow,
}: PostAnalyticsDataType): AnalyticsCartItemType[] => {
  const { lines } = cart || {};

  if (lines && hasArrayElements(lines)) {
    const mergedLines = getMergedLines(lines);

    return mergedLines?.map((lineItem, index) =>
      getLineItemData({ lineItem, source, buyFlow, index }),
    );
  }
  return [] as AnalyticsCartItemType[];
};

const getLineItemData = ({
  lineItem,
  source,
  buyFlow,
  index,
}: {
  lineItem?: ExtendedCheckoutLines;
  source?: string;
  buyFlow?: string;
  index?: number;
}) => {
  if (lineItem && hasArrayElements(Object.keys(lineItem))) {
    const { quantity, variant, appliedPromotions, totalPrice, selectedQuantity } = lineItem;
    const {
      name: variantName,
      product,
      pricing,
      quantityAvailable,
      attributes,
      sku,
    } = variant || {};
    const { name, brand, category } = product || {};

    const productName =
      (extractValueBySlug("en", attributes, SaleorAttributes.TITLE) as string) ||
      (getAttributeValue(attributes, "defaultName") as string) ||
      variantName ||
      name ||
      "Unknown Product";

    let promotionNames = "";
    if (Array.isArray(appliedPromotions)) {
      promotionNames = appliedPromotions
        .map((each) => each?.promotion?.name || each?.name)
        .join(":");
    }

    const updatedQuantity = getQuantity(selectedQuantity) ?? getQuantity(quantity) ?? 1;
    const variantMedia =
      getProductImages("en", attributes, "product_image") ??
      getProductImages("en", attributes, "cl_product_image") ??
      [];
    const lensPrice = calculateLensPrice(lineItem);

    try {
      const price = parsePrice(totalPrice) ?? 0.0;
      const undiscountedPrice =
        parsePrice(pricing?.costPrice) ?? parsePrice(pricing?.priceUndiscounted) ?? 0.0;
      const [appliedPromotion] = (hasArrayElements(appliedPromotions) && appliedPromotions) || [];
      let priceDetails = null;
      if (appliedPromotion?.promotion?.type === promotionType.BUYXGETY && price) {
        priceDetails = getPriceForBuyXgetY(undiscountedPrice, price, quantity);
      } else {
        priceDetails = getCalculatedPriceInfo(
          undiscountedPrice,
          price / updatedQuantity,
          appliedPromotion,
        );
      }

      const constructedData: AnalyticsDataType = {
        currency: totalPrice?.currency ?? "",
        value: price + lensPrice?.price,
        item_list_name: source ?? "",
        item_list_id: "",
        index: index ? ++index : 1,
        item_id: sku?.toString() ?? "",
        item_name: productName,
        item_brand: brand?.name || "",
        item_category: category?.name || "",
        item_variant: productName || "",
        buy_flow: buyFlow,
        price: undiscountedPrice + lensPrice?.undiscountedPrice,
        discount: calculateDiscount(
          undiscountedPrice + lensPrice?.undiscountedPrice,
          priceDetails?.discountedPrice + lensPrice?.price,
        ),
        quantity: updatedQuantity,
        discount_percent: calculateDiscountPercent(
          undiscountedPrice + lensPrice?.undiscountedPrice,
          priceDetails?.discountedPrice + lensPrice?.price,
        ),
        product_image: variantMedia?.[0] ?? "",
        stock_status: quantityAvailable && quantityAvailable > 0 ? "In Stock" : "Out of Stock",
        sale_status: getOfferStatus(priceDetails?.hasDiscount),
        product_gender: getGender(attributes),
        discount_name: promotionNames,

        //Add these values
        coupon: "",
        item_category2: "",
        item_category3: "",
        item_category4: "",
        item_category5: "",
      };
      return constructedData;
    } catch (error) {
      console.error(error);
    }
  }
  return {} as AnalyticsDataType;
};

const getCartDetails = ({ cart }: PostAnalyticsDataType) => {
  const {
    totalPrice,
    giftWrap,
    lines,
    shippingAddress,
    voucherCode,
    deliveryMethod,
    shippingPrice,
  } = cart || {};
  const mergedLines = Array.isArray(lines) ? getMergedLines(lines) : [];

  const cartDetails: CartDetails = {
    value: parseCartPrice(totalPrice?.gross) ?? 0.0,
    currency: totalPrice?.gross?.currency ?? "",

    coupon: voucherCode ?? "",
    giftAdded: giftWrap?.giftWrappingEnabled ? "Yes" : "No",
    giftPrice: parseCartPrice(giftWrap?.price) ?? 0.0,

    shippingCountry: shippingAddress?.country?.country ?? "",
    shippingCity: shippingAddress?.city ?? "",
    shippingTier: deliveryMethod?.name ?? "",
    shippingMethod: deliveryMethod?.name ?? "",
    shippingArea: shippingAddress?.cityArea ?? "",
    shippingAddress: shippingAddress?.streetAddress1 ?? "",

    shipping: parseCartPrice(shippingPrice?.gross) ?? 0.0,
    tax: parseCartPrice(totalPrice?.tax) ?? 0.0,

    numberOfItems: Array.isArray(mergedLines) ? mergedLines.length : 0,
  };
  return cartDetails;
};

const getPaymentType = (order?: Order): string => {
  const paymentIntentValue = order?.metadata?.find(
    (meta: SaleorMetadata) => meta.key === "paymentIntent",
  )?.value;
  const cod = order?.cashOnDelivery;

  if (paymentIntentValue) {
    try {
      const { paymentType, source_data, gateway } = JSON.parse(paymentIntentValue);

      if (paymentType === paymentTypes.CARD && source_data?.card_type) {
        return `${source_data?.card_type?.toLowerCase()} card`;
      }

      if (gateway === paymentTypes.PAYMOB) {
        return paymentConstants.paymentMethods.PAYMOB_CARD?.toLowerCase() ?? "";
      }

      return paymentType === paymentTypes.GPAY
        ? (paymentConstants.paymentMethods.GPAY?.toLowerCase() ?? "")
        : (paymentType?.toLowerCase() ?? "");
    } catch (error) {
      console.error("Error parsing paymentIntentValue:", error);
    }
  }

  return cod ? "cod" : "";
};

const getSaleorOrderDetails = ({ saleorOrder }: PostAnalyticsDataType) => {
  const { giftWrap, lines, shippingAddress, deliveryMethod, shippingPrice, voucher, total } =
    saleorOrder || {};
  const mergedLines = Array.isArray(lines) ? getMergedLines(lines) : [];
  const cartDetails: CartDetails = {
    value: parseCartPrice(total?.gross) ?? 0.0,
    currency: total?.gross?.currency ?? "",

    coupon: voucher?.code ?? "",
    giftAdded: giftWrap?.giftWrappingEnabled ? "Yes" : "No",
    giftPrice: parseCartPrice(giftWrap?.price) ?? 0.0,

    shippingCountry: shippingAddress?.country?.country ?? "",
    shippingCity: shippingAddress?.city ?? "",
    shippingTier: deliveryMethod?.name ?? "",
    shippingMethod: deliveryMethod?.name ?? "",
    shippingArea: shippingAddress?.cityArea ?? "",
    shippingAddress: shippingAddress?.streetAddress1 ?? "",

    shipping: parseCartPrice(shippingPrice?.gross) ?? 0.0,
    tax: parseCartPrice(total?.tax) ?? 0.0,

    numberOfItems: Array.isArray(mergedLines) ? mergedLines.length : 0,

    paymentType: getPaymentType(saleorOrder) ?? "",
  };
  return cartDetails;
};

const getSaleorOrderLineItems = ({ saleorOrder }: PostAnalyticsDataType) => {
  const { lines } = saleorOrder || {};
  const mergedLines = getUpdatedLineItems(lines);
  if (mergedLines && hasArrayElements(mergedLines)) {
    return mergedLines?.map((lineItem: OrderLines, index: number) => {
      if (lineItem && hasArrayElements(Object.keys(lineItem))) {
        const { quantity, variant, appliedPromotions, totalPrice } = lineItem;
        const {
          name: variantName,
          product,
          pricing,
          quantityAvailable,
          attributes,
          sku,
        } = variant || {};
        const { name, brand, category } = product || {};
        let promotionNames = "";

        if (Array.isArray(appliedPromotions)) {
          promotionNames = appliedPromotions
            .map((each) => each?.promotion?.name || each?.name)
            .join(":");
        }

        const updatedQuantity = getQuantity(quantity) ?? 1;

        const variantMedia = getVariantImages(attributes);
        const productName =
          (getAttributeValue(attributes, SaleorAttributes.TITLE) as string) || name;

        const lensPrice = calculateLensPrice(lineItem);

        try {
          const price = parsePrice(totalPrice) ?? 0.0;
          const undiscountedPrice =
            parsePrice(pricing?.costPrice) ?? parsePrice(pricing?.priceUndiscounted) ?? 0.0;
          const [appliedPromotion] =
            (hasArrayElements(appliedPromotions) && appliedPromotions) || [];
          let priceDetails = null;
          if (appliedPromotion?.promotion?.type === promotionType.BUYXGETY && price) {
            priceDetails = getPriceForBuyXgetY(undiscountedPrice, price, quantity);
          } else {
            priceDetails = getCalculatedPriceInfo(
              undiscountedPrice,
              price / updatedQuantity,
              appliedPromotion,
            );
          }
          const constructedData: AnalyticsDataType = {
            currency: totalPrice?.gross?.currency ?? "",
            value: price + lensPrice?.price,
            index: index ? ++index : 1,
            item_id: sku?.toString() ?? "",
            item_name: productName || name || "",
            item_brand: brand?.name || "",
            item_category: category?.name || "",
            item_variant: variantName || "",
            price: undiscountedPrice + lensPrice?.undiscountedPrice,
            discount: calculateDiscount(
              undiscountedPrice + lensPrice?.undiscountedPrice,
              priceDetails?.discountedPrice + lensPrice?.price,
            ),
            quantity: updatedQuantity,
            discount_percent: calculateDiscountPercent(
              undiscountedPrice + lensPrice?.undiscountedPrice,
              priceDetails?.discountedPrice + lensPrice?.price,
            ),
            product_image: variantMedia?.[0]?.url ?? "",
            stock_status: quantityAvailable && quantityAvailable > 0 ? "In Stock" : "Out of Stock",
            sale_status: getOfferStatus(priceDetails?.hasDiscount),
            product_gender: getGender(attributes),
            discount_name: promotionNames,

            //Add these values
            item_list_id: "",
            item_list_name: "",
            coupon: "",
            item_category2: "",
            item_category3: "",
            item_category4: "",
            item_category5: "",
          };
          return constructedData;
        } catch (error) {
          console.error(error);
        }
      }
      return {} as AnalyticsDataType;
    });
  }
  return [];
};

const getOrderLineItems = ({ order }: PostAnalyticsDataType) => {
  const { lineItems } = order || {};
  if (lineItems && hasArrayElements(lineItems)) {
    return lineItems.map((lineItem, index) => {
      const constructedData: AnalyticsCartItemType = {
        item_id: lineItem?.productId,
        item_name: lineItem?.itemName,
        discount: lineItem?.discount,
        item_brand: lineItem?.brandName,
        index: index + 1,
        item_variant: lineItem?.color,
        price: lineItem?.grossPrice,
        quantity: lineItem?.quantity,
        stock_status: (lineItem?.store?.availableStock || 0) > 0 ? "In Stock" : "Out of Stock",
        sale_status: getOfferStatus(lineItem?.discountPercent > 0),
        discount_percent: `${lineItem?.discountPercent || 0}%`,
        item_category: lineItem?.itemGroupCategoryName,
        product_gender: "",
        coupon: "",
        item_category2: "",
        item_category3: "",
        item_category4: "",
        item_category5: "",
        item_list_id: "",
        item_list_name: "",
      };
      return constructedData;
    });
  }
  return [];
};

const getOrderDetails = ({ order }: PostAnalyticsDataType) => {
  const { priceInfo } = order || {};
  const orderDetails: OrderDetails = {
    orderId: order?.orderNo ?? "",
    currency: priceInfo?.currency ?? "",
    value: priceInfo?.basePrice ?? 0.0,
    coupon: "",
    shipping: priceInfo?.deliveryCharge ?? 0.0,
    tax: priceInfo?.tax?.netAmount ?? 0.0,
  };
  return orderDetails;
};

const getLineItemDetails = ({ lineItem }: PostAnalyticsDataType) => {
  const { totalPrice, variant } = lineItem || {};
  const { attributes } = variant || {};
  const variantMedia = hasArrayElements(attributes || [])
    ? getVariantImages(attributes)
    : undefined;
  const lineItemDetails: { value: number; currency: string; image: string } = {
    value: parseCartPrice(totalPrice?.gross) ?? 0.0,
    currency: totalPrice?.gross?.currency ?? "",
    image: variantMedia?.[0]?.url ?? "",
  };
  return lineItemDetails;
};

const getAddOnLensPrice = (addOnSkus?: LCSkuType) => {
  const { left, right } = addOnSkus ?? {};
  return {
    price: (left?.pricing?.price?.gross?.amount ?? 0) + (right?.pricing?.price?.gross?.amount ?? 0),
    discount:
      (left?.pricing?.discount?.gross?.amount ?? 0) +
      (right?.pricing?.discount?.gross?.amount ?? 0),
    undiscounted:
      (left?.pricing?.priceUndiscounted?.gross?.amount ?? 0) +
      (right?.pricing?.priceUndiscounted?.gross?.amount ?? 0),
  };
};

const getProductsData = ({
  products,
  source,
  sourceId,
}: PostAnalyticsDataType): AnalyticsCartItemType[] =>
  Array.isArray(products)
    ? products.map((product, index) =>
      getSubData(getProductData(product, source, sourceId, undefined, undefined, index)),
    )
    : [];

const getProductData = (
  product: RecommendProductItem | AlgoliaProductItem | Product,
  source?: string,
  sourceId?: string,
  selectedVariantId?: string,
  buyFlow?: string,
  index?: number,
) => {
  if (product && hasArrayElements(Object.keys(product))) {
    const {
      brand,
      name,
      defaultName,
      defaultCategoryName,
      defaultBrandName,
      category,
      variants,
      variantColorMappings,
      categories,
      objectID,
      defaultVariant,
      hitIndex, // For PLP product card index
      selectedQuantity, // For user selected quantity
      addOnSkus, //For Eyeglasses
      variant_color,
    } = product;
    // VariantColorMappings used as variants in Algolia response
    const finalVariants =
      (hasArrayElements(variantColorMappings) && variantColorMappings) || variants;

    const lensPrice = getAddOnLensPrice(addOnSkus);

    if (finalVariants && hasArrayElements(finalVariants)) {
      let selectedVariant: VariantItemPdp & VariantItem;

      if (objectID) {
        selectedVariant =
          finalVariants?.find(
            (item: VariantItem) => item.variantId === defaultVariant?.variantId && item.inStock,
          ) ?? finalVariants?.[0];
      } else {
        selectedVariant =
          finalVariants.find(
            (variant: VariantItemPdp & VariantItem) =>
              variant?.id === selectedVariantId || variant?.variantId === selectedVariantId,
          ) ?? finalVariants?.[0];
      }

      const {
        sku,
        currency,
        pricing,
        quantity,
        quantityAvailable,
        variantNameDefault,
        attributes,
        defaultAttributes,
        availablePromotions,
        availablePromotion: availablePromotionsAlgolia,
        media,
      } = (selectedVariant as VariantItem & VariantItemPdp) ?? {};

      const { costPrice, priceUndiscounted, price: priceInfo } = pricing || {};
      const price = parsePrice(priceInfo) ?? parseAlgoliaPrice(priceInfo) ?? 0.0;
      const promotions = (objectID && availablePromotionsAlgolia) || availablePromotions;
      const [promotionInfo] = (hasArrayElements(promotions) && promotions) || [];
      const undiscountedPrice: number =
        parsePrice(costPrice) ??
        parsePrice(priceUndiscounted) ??
        parseAlgoliaPrice(costPrice) ??
        parseAlgoliaPrice(priceUndiscounted) ??
        0.0;
      const priceDetails = getCalculatedPriceInfo(undiscountedPrice, price, promotionInfo);
      const availableQuantity = getQuantity(quantity) ?? getQuantity(quantityAvailable) ?? 0;

      // console.log("variant", objectID, media, hasArrayElements(attributes), getProductImages("en", attributes, "product_image"))

      const variantMedia: MediaItem[] =
        (objectID && media) ||
        (hasArrayElements(attributes) &&
          getProductImages("en", attributes, "product_image")?.map((v) => ({
            url: v,
            type: "product",
          }))) ||
        getProductImages("en", attributes, "cl_product_image")?.map((v) => ({
          url: v,
          type: "product",
        })) ||
        [];

      // const variantName = !objectID
      //   ? (getAttributeValue(attributes, SaleorAttributes.TITLE) as string)
      //   : "";
      const variantName = !objectID
        ? (extractValueBySlug("en", attributes, SaleorAttributes.TITLE) as string)
        : "";

      const updatedQuantity = getQuantity(selectedQuantity) ?? getQuantity(quantity) ?? 0;

      try {
        const constructedData: AnalyticsDataType = {
          currency: currency || priceInfo?.currency,
          value: price + lensPrice?.price,
          item_list_name: source ?? "",
          item_list_id: sourceId ?? "",
          product_image: variantMedia?.[0]?.url ?? "",
          index: hitIndex ?? (index ?? 0) + 1,
          item_id: sku ?? "",
          item_name: defaultName || name || "",
          item_brand: defaultBrandName || brand?.name || "",
          item_category: defaultCategoryName || category?.name || categories?.["lvl0"] || "",
          item_variant: (variantNameDefault || variantName).concat(`-${variant_color}`) || "",
          buy_flow: buyFlow,
          price: undiscountedPrice + lensPrice?.undiscounted,
          discount: calculateDiscount(
            undiscountedPrice + lensPrice?.undiscounted,
            priceDetails?.discountedPrice + lensPrice?.price,
          ),
          discount_percent: calculateDiscountPercent(
            undiscountedPrice + lensPrice?.undiscounted,
            priceDetails?.discountedPrice + lensPrice?.price,
          ),
          sale_status: getOfferStatus(priceDetails?.hasDiscount),
          quantity: updatedQuantity,
          stock_status: availableQuantity > 0 ? "In Stock" : "Out of Stock",
          product_gender: selectedVariant?.genderEnum || getGender(defaultAttributes || attributes),

          //Add these values
          discount_name:
            promotionInfo?.promotion?.defaultName || promotionInfo?.promotion?.name || "",
          coupon: "",
          item_category2: "",
          item_category3: "",
          item_category4: "",
          item_category5: "",
        };
        return constructedData;
      } catch (error) {
        console.error(error);
      }
    }
  }
  return {} as AnalyticsDataType;
};

const getSubData = (dataIn: AnalyticsDataType): AnalyticsDataType => {
  const data: AnalyticsDataType = {
    item_name: dataIn.item_name,
    item_id: dataIn.item_id,
    price: dataIn.price,
    value: dataIn.value,
    currency: dataIn.currency,
    item_brand: dataIn.item_brand,
    item_category: dataIn.item_category,
    item_category2: dataIn.item_category2,
    item_category3: dataIn.item_category3,
    item_category4: dataIn.item_category4,
    item_category5: dataIn.item_category5,
    index: dataIn.index,
    coupon: dataIn.coupon,
    discount: dataIn.discount,
    discount_name: dataIn.discount_name,
    product_image: dataIn.product_image,
    discount_percent: dataIn.discount_percent,
    item_list_id: dataIn.item_list_id,
    item_list_name: dataIn.item_list_name,
    item_variant: dataIn.item_variant,
    product_gender: dataIn.product_gender,
    sale_status: dataIn.sale_status,
    stock_status: dataIn.stock_status,
    quantity: dataIn.quantity,
  };

  if (dataIn.buy_flow) {
    data.buy_flow = dataIn.buy_flow;
  }

  return data;
};

const getVariantData = (variant?: ExtendedVariantItem, source?: string, buyFlow?: string) => {
  const {
    sku,
    currency,
    pricing,
    quantity,
    quantityAvailable,
    attributes,
    availablePromotions,
    product,
    selectedQuantity,
  } = variant ?? {};

  const { brand, name, category } = product || {};

  const { costPrice, priceUndiscounted, price: priceInfo } = pricing || {};

  const price = parsePrice(priceInfo) ?? 0.0;
  const [promotionInfo] = Array.isArray(availablePromotions) ? availablePromotions : [];
  const undiscountedPrice: number = parsePrice(costPrice) ?? parsePrice(priceUndiscounted) ?? 0.0;
  const priceDetails = getCalculatedPriceInfo(undiscountedPrice, price, promotionInfo);
  const availableQuantity = getQuantity(quantity) ?? getQuantity(quantityAvailable) ?? 0;
  const variantMedia: MediaItem[] = Array.isArray(attributes) ? getVariantImages(attributes) : [];
  const variantName = (getAttributeValue(attributes, SaleorAttributes.TITLE) as string) ?? "";
  const updatedQuantity = getQuantity(selectedQuantity) ?? 0;

  try {
    const constructedData: AnalyticsDataType = {
      currency: currency || priceInfo?.currency,
      value: price,
      item_list_name: source ?? "",
      item_list_id: "",
      product_image: variantMedia?.[0]?.url ?? "",
      index: 1,
      item_id: sku ?? "",
      item_name: name ?? "",
      item_brand: brand?.name ?? "",
      item_category: category?.name ?? "",
      item_variant: variantName,
      buy_flow: buyFlow,
      price: undiscountedPrice,
      discount: calculateDiscount(undiscountedPrice, priceDetails?.discountedPrice),
      discount_percent: calculateDiscountPercent(undiscountedPrice, priceDetails?.discountedPrice),
      sale_status: getOfferStatus(priceDetails?.hasDiscount),
      quantity: updatedQuantity,
      stock_status: availableQuantity > 0 ? "In Stock" : "Out of Stock",
      product_gender: getGender(attributes),

      //Add these values
      discount_name: promotionInfo?.promotion?.name ?? "",
      coupon: "",
      item_category2: "",
      item_category3: "",
      item_category4: "",
      item_category5: "",
    };
    return constructedData;
  } catch (error) {
    console.error(error);
    return {} as AnalyticsDataType;
  }
};

const getLensVariantPrice = (addOns?: CheckoutLines[]) => {
  return {
    price:
      addOns?.reduce((acc, item) => {
        return acc + (item?.variant?.pricing?.price?.gross?.amount ?? 0);
      }, 0) ?? 0,
    undiscountedPrice:
      addOns?.reduce((acc, item) => {
        return acc + (item?.variant?.pricing?.priceUndiscounted?.gross?.amount ?? 0);
      }, 0) ?? 0,
  };
};

const getLineItemVariantData = (
  lineItem?: ExtendedCheckoutLines,
  source?: string,
  buyFlow?: string,
) => {
  if (lineItem && hasArrayElements(Object.keys(lineItem))) {
    const { variant, selectedQuantity } = lineItem;
    const {
      sku,
      pricing,
      quantityAvailable,
      attributes,
      availablePromotions,
      product,
      name: variantName,
    } = variant ?? {};

    const { brand, name, category } = product || {};

    const { costPrice, priceUndiscounted, price: priceInfo } = pricing || {};

    const lensPrice = getLensVariantPrice(lineItem?.addOns);

    const price = parsePrice(priceInfo) ?? 0.0;
    const [promotionInfo] = Array.isArray(availablePromotions) ? availablePromotions : [];
    const undiscountedPrice: number = parsePrice(costPrice) ?? parsePrice(priceUndiscounted) ?? 0.0;
    const priceDetails = getCalculatedPriceInfo(undiscountedPrice, price, promotionInfo);
    const availableQuantity = getQuantity(quantityAvailable) ?? 0;
    const variantMedia: MediaItem[] = Array.isArray(attributes) ? getVariantImages(attributes) : [];
    const updatedQuantity = getQuantity(selectedQuantity) ?? 0;
    const productName = (getAttributeValue(attributes, SaleorAttributes.TITLE) as string) || name;
    try {
      const constructedData: AnalyticsDataType = {
        currency: priceInfo?.currency,
        value: price + lensPrice?.price,
        item_list_name: source ?? "",
        item_list_id: "",
        product_image: variantMedia?.[0]?.url ?? "",
        index: 1,
        item_id: sku?.toString() ?? "",
        item_name: productName ?? "",
        item_brand: brand?.name ?? "",
        item_category: category?.name ?? "",
        item_variant: variantName ?? "",
        buy_flow: buyFlow,
        price: undiscountedPrice + lensPrice?.undiscountedPrice,
        discount: calculateDiscount(
          undiscountedPrice + lensPrice?.undiscountedPrice,
          priceDetails?.discountedPrice + lensPrice?.price,
        ),
        discount_percent: calculateDiscountPercent(
          undiscountedPrice + lensPrice?.undiscountedPrice,
          priceDetails?.discountedPrice + lensPrice?.price,
        ),
        sale_status: getOfferStatus(priceDetails?.hasDiscount),
        quantity: updatedQuantity,
        stock_status: availableQuantity > 0 ? "In Stock" : "Out of Stock",
        product_gender: getGender(attributes),
        discount_name: promotionInfo?.promotion?.name ?? "",

        //Add these values
        coupon: "",
        item_category2: "",
        item_category3: "",
        item_category4: "",
        item_category5: "",
      };
      return constructedData;
    } catch (error) {
      console.error(error);
    }
  }
  return {} as AnalyticsDataType;
};

const getPromotionListData = (promotions?: PromotionalBannerItem[], source?: string) => {
  return promotions && Array.isArray(promotions)
    ? promotions.map((promotion, index) => getPromotionData(promotion, index, source))
    : [];
};

const getPromotionData = (promotion: PromotionalBannerItem, index: number, source?: string) => {
  const { defaultTitle, title, type, key } = promotion || {};

  //Update these data after sanity configuration of promotion id
  const constructedData: PromotionItem = {
    promotion_id: defaultTitle || title || key || "",
    promotion_name: defaultTitle || title || "",
    creative_name: type || "",
    creative_slot: source ? `${source}_${++index}` : "",
    location_id: "",
  };
  return constructedData;
};

const handleContentActionsEvent = (rawData: PostAnalyticsDataType) => {
  let payload: EventType = {} as EventType;
  if (rawData && rawData.target) {
    switch (rawData.target) {
      case analyticEvents.PRODUCT_CLICK: {
        const details = getProductData(
          rawData.product,
          rawData.source,
          rawData.sourceId,
          rawData.selectedVariantId,
        );
        payload = {
          event: analyticEvents.PRODUCT_CLICK,
          ecommerce: {
            item_list_id: rawData?.sourceId ?? "",
            item_list_name: rawData?.source ?? "",
            currency: details?.currency ?? "",
            items: [getSubData(details)],
          },
        };
        break;
      }

      case analyticEvents.ADD_TO_CART: {
        const details = getProductData(
          rawData.product,
          rawData.source,
          rawData.sourceId,
          rawData.selectedVariantId,
          rawData.buyFlow,
        );
        console.log(rawData, "rawData");
        console.log(details, "details");
        payload = {
          event: analyticEvents.ADD_TO_CART,
          ecommerce: {
            item_list_id: rawData?.sourceId ?? "",
            item_list_name: rawData?.source ?? "",
            currency: details?.currency ?? "",
            value: details?.value,
            items: [getSubData(details)],
          },
        };
        break;
      }

      case analyticEvents.ADD_TO_WISHLIST: {
        const details = rawData.product
          ? getProductData(
            rawData.product,
            rawData.source,
            rawData.sourceId,
            rawData.selectedVariantId,
          )
          : getLineItemData(rawData);
        payload = {
          event: analyticEvents.ADD_TO_WISHLIST,
          ecommerce: {
            item_list_id: rawData?.sourceId ?? "",
            item_list_name: rawData?.source ?? "",
            currency: details?.currency ?? "",
            value: details?.value,
            items: [getSubData(details)],
          },
        };
        break;
      }

      case analyticEvents.REMOVE_FROM_CART: {
        const details = getLineItemDetails(rawData);
        payload = {
          event: analyticEvents.REMOVE_FROM_CART,
          ecommerce: {
            item_list_name: rawData?.source || "",
            item_list_id: "",
            currency: details?.currency || "",
            value: details?.value,
            items: [getLineItemData(rawData)],
          },
        };
        break;
      }

      case analyticEvents.ADD_SHIPPING_INFO: {
        const details = getCartDetails(rawData);
        const cartItems = getCartLineItems(rawData);

        payload = {
          event: analyticEvents.ADD_SHIPPING_INFO,
          ecommerce: {
            item_list_name: "",
            item_list_id: "",
            currency: details?.currency || "",
            value: details?.value,
            items: cartItems,
            coupon: details?.coupon || "",
            Gift_added: details?.giftAdded || "",
            Gift_price: details?.giftPrice || 0,
            shipping_country: details?.shippingCountry || "",
            shipping_city: details?.shippingCity || "",
            shipping_tier: details?.shippingTier || "",
            shipping_method: details?.shippingMethod || "",
            shipping_area: details?.shippingArea || "",
            shipping_address: details?.shippingAddress || "",
          },
        };
        break;
      }

      case analyticEvents.SELECT_PROMOTION: {
        payload = {
          event: analyticEvents.SELECT_PROMOTION,
          ecommerce: {
            items: getPromotionListData(rawData.promotions, rawData?.source),
          },
        };
        break;
      }

      case analyticEvents.ADD_PAYMENT_INFO: {
        const details = getCartDetails(rawData);
        payload = {
          event: analyticEvents.ADD_PAYMENT_INFO,
          ecommerce: {
            item_list_name: "",
            item_list_id: "",
            currency: details?.currency || "",
            value: details?.value,
            items: getCartLineItems(rawData),
            coupon: details?.coupon || "",
            Gift_added: details?.giftAdded || "",
            Gift_price: details?.giftPrice || 0,
            shipping_country: details?.shippingCountry || "",
            shipping_city: details?.shippingCity || "",
            shipping_tier: details?.shippingTier || "",
            payment_type: rawData?.paymentType || "",
          },
        };
        break;
      }

      case analyticEvents.PURCHASE: {
        const details = getSaleorOrderDetails(rawData);

        const isCNC = rawData?.saleorOrder?.deliveryMethod?.__typename === "Warehouse";

        let firstName = "";
        let lastName = "";
        let phone = "";

        if (isCNC) {
          // for click and collect, get customer details from billing-address metadata
          const personalDetailsMetadata = rawData?.saleorOrder?.billingAddress?.metadata?.find(
            (meta: SaleorMetadata) => meta?.key === "personalDetails"
          )?.value;

          if (personalDetailsMetadata) {
            try {
              const personalDetails = JSON.parse(personalDetailsMetadata);
              firstName = personalDetails?.firstName || "";
              lastName = personalDetails?.lastName || "";
              phone = personalDetails?.phone || "";
            } catch (error) {
              console.error("Error parsing personalDetails metadata:", error);
            }
          }

        } else {
          // for home delivery, use shippingAddress
          firstName = rawData?.saleorOrder?.shippingAddress?.firstName || "";
          lastName = rawData?.saleorOrder?.shippingAddress?.lastName || "";
          phone = rawData?.saleorOrder?.shippingAddress?.phone || "";
        }

        payload = {
          event: analyticEvents.PURCHASE,
          purchaseUserEmail: rawData?.saleorOrder?.userEmail || "",
          purchaseUserFirstName: firstName,
          purchaseUserLastName: lastName,
          purchaseUserPhoneNumber: phone,
          purchaseUserAddress: rawData?.saleorOrder?.shippingAddress?.streetAddress1 || "",
          purchaseCustomerId: rawData?.saleorOrder?.user?.magrabiId || "",
          ecommerce: {
            item_list_name: "",
            item_list_id: "",
            currency: details?.currency || "",
            value: details?.value,
            items: getSaleorOrderLineItems(rawData),
            coupon: details?.coupon || "",
            Gift_added: details?.giftAdded || "",
            Gift_price: details?.giftPrice || 0,
            shipping_country: details?.shippingCountry || "",
            shipping_city: details?.shippingCity || "",
            shipping_tier: details?.shippingTier || "",
            transaction_id: rawData?.orderId ?? "",
            tax: details?.tax,
            shipping: details?.shipping,
            payment_method: details?.paymentType ?? "",
          },
        };
        break;
      }

      case analyticEvents.REFUND: {
        const details = getOrderDetails(rawData);
        payload = {
          event: analyticEvents.REFUND,
          ecommerce: {
            currency: details?.currency || "",
            transaction_id: details?.orderId || "",
            value: details?.value,
            coupon: details?.coupon || "",
            shipping: details?.shipping,
            tax: details?.tax,
            items: getOrderLineItems(rawData),
          },
        };
        break;
      }

      case "ADD_TO_CART_WISHLIST": {
        const details = getVariantData(rawData.variant, rawData.source, rawData.buyFlow);
        payload = {
          event: analyticEvents.ADD_TO_CART,
          ecommerce: {
            item_list_id: rawData?.sourceId ?? "",
            item_list_name: rawData?.source ?? "",
            currency: details?.currency ?? "",
            value: details?.value,
            items: [getSubData(details)],
          },
        };
        break;
      }

      case analyticEvents.QUANTITY_ADJUSTMENT: {
        const details = getLineItemVariantData(rawData.lineItem, rawData.source, rawData.buyFlow);
        payload = {
          event: rawData?.isQuantityIncrement
            ? analyticEvents.ADD_TO_CART
            : analyticEvents.REMOVE_FROM_CART,
          ecommerce: {
            item_list_name: rawData?.source || "",
            item_list_id: "",
            currency: details?.currency || "",
            value: details?.value,
            items: [getSubData(details)],
          },
        };
        break;
      }

      default:
        console.warn("invalid click target.");
    }
  }
  return payload;
};

const handleContentDetailsEvent = (rawData: PostAnalyticsDataType) => {
  let payload: EventType | CartItemsEvent = {} as EventType;
  if (rawData?.target) {
    switch (rawData.target) {
      case analyticEvents.VIEW_ITEM: {
        const details = getProductData(
          rawData.product,
          rawData.source,
          rawData.sourceId,
          rawData.selectedVariantId,
        );
        payload = {
          event: analyticEvents.VIEW_ITEM,
          ecommerce: {
            currency: details?.currency ?? "",
            value: details?.value,
            item_list_id: rawData?.sourceId ?? "",
            item_list_name: rawData?.source ?? "",
            items: [getSubData(details)],
          },
        };
        break;
      }

      case analyticEvents.VIEW_CART: {
        const details = getCartDetails(rawData);
        payload = {
          event: analyticEvents.VIEW_CART,
          ecommerce: {
            item_list_name: rawData?.source ?? "",
            item_list_id: "",
            currency: (details?.currency || rawData?.currency) ?? "",
            value: details?.value,
            items: getCartLineItems(rawData),
          },
        };
        break;
      }

      case analyticEvents.BEGIN_CHECKOUT: {
        const details = getCartDetails(rawData);
        payload = {
          event: analyticEvents.BEGIN_CHECKOUT,
          ecommerce: {
            item_list_name: rawData?.source ?? "",
            item_list_id: "",
            currency: details?.currency || "",
            value: details?.value,
            items: getCartLineItems(rawData),
            coupon: details?.coupon || "",
            Gift_added: details?.giftAdded || "",
            Gift_price: details?.giftPrice || 0,
          },
        };
        break;
      }

      case analyticEvents.VIEW_ITEM_LIST: {
        payload = {
          event: analyticEvents.VIEW_ITEM_LIST,
          ecommerce: {
            item_list_id: rawData?.sourceId || "",
            item_list_name: rawData?.source || "",
            currency: rawData?.currency || "",
            items: getProductsData(rawData),
          },
        };
        break;
      }

      case analyticEvents.VIEW_PROMOTION: {
        payload = {
          event: analyticEvents.VIEW_PROMOTION,
          ecommerce: {
            items: getPromotionListData(rawData.promotions, rawData?.source),
          },
        };
        break;
      }

      case analyticEvents.CART_ITEMS: {
        const details = getCartDetails(rawData);
        payload = {
          event: analyticEvents.CART_ITEMS,
          CartProducts: details.numberOfItems > 0 ? "Not Empty" : "Empty",
          productInCart: details.numberOfItems,
          ecommerce: {
            currency: details?.currency || rawData?.currency || "",
            value: details?.value,
            items: getCartLineItems(rawData),
          },
        };
        break;
      }
      default:
        console.warn("invalid view target.");
    }
  }
  return payload;
};

const handleCustomEvent = (rawData: PostAnalyticsDataType) => {
  let payload: EventType = {} as EventType;
  if (rawData?.target) {
    switch (rawData.target) {
      case analyticEvents.SIGN_UP: {
        payload = {
          event: analyticEvents.SIGN_UP,
          method: rawData.method || "",
          ...rawData.userData,
        };
        break;
      }

      case analyticEvents.LOGIN: {
        payload = {
          event: analyticEvents.LOGIN,
          method: rawData.method ?? "",
          ...rawData.userData,
        };
        break;
      }

      case analyticEvents.COUPON_CODE_VALID: {
        payload = {
          event: analyticEvents.COUPON_CODE_VALID,
          CouponCode: rawData?.couponData?.couponCode ?? "",
        };
        break;
      }

      case analyticEvents.COUPON_CODE_INVALID: {
        payload = {
          event: analyticEvents.COUPON_CODE_INVALID,
          CouponCode: rawData?.couponData?.couponCode ?? "",
          Error: rawData?.couponData?.error ?? "",
        };
        break;
      }

      case analyticEvents.GIFT_ADDED: {
        payload = {
          event: analyticEvents.GIFT_ADDED,
          Notes: rawData?.giftData?.notes ?? "",
          price: rawData?.giftData?.price ?? 0,
        };
        break;
      }

      case analyticEvents.NEWSLETTER_SUBSCRIPTION: {
        payload = {
          event: analyticEvents.NEWSLETTER_SUBSCRIPTION,
          userEmail: rawData?.email ?? "",
          hashedEmail: rawData?.email ? generateHash(rawData?.email) : "",
        };
        break;
      }

      case analyticEvents.FORM_SUBMISSION: {
        payload = {
          event: analyticEvents.FORM_SUBMISSION,
          formId: rawData?.formData?.formId ?? "",
          formType: rawData?.formData?.formType ?? "",
        };
        break;
      }

      case analyticEvents.TRACK_ORDER: {
        payload = {
          event: analyticEvents.TRACK_ORDER,
          order_ID: rawData?.orderId ?? "",
        };
        break;
      }

      case analyticEvents.BOOK_EYE_TEST:
        payload = {
          event: analyticEvents.BOOK_EYE_TEST,
          eventAction: rawData?.bookEyeTestData?.eventAction ?? "",
          eventLabel: rawData?.bookEyeTestData?.eventLabel ?? "",
        };
        break;

      case analyticEvents.BOOK_EYE_TEST_SUCCESS:
        payload = {
          event: analyticEvents.BOOK_EYE_TEST_SUCCESS,
          TestType: rawData?.bookEyeTestData?.testType ?? "",
          StoreName: rawData?.bookEyeTestData?.storeName ?? "",
          DateTime: rawData?.bookEyeTestData?.dateTime ?? "",
        };
        break;

      case analyticEvents.CHECKOUT_ERROR:
        payload = {
          event: analyticEvents.CHECKOUT_ERROR,
          message: rawData?.message ?? "",
        };
        break;

      case analyticEvents.CUSTOM_PAGE_VIEW:
        payload = {
          event: analyticEvents.CUSTOM_PAGE_VIEW,
          ...createGaInitialObject(rawData?.locale ?? "", rawData?.user, rawData?.gaPageInfo),
        };
        break;
    }
  }
  return payload;
};

export const handlePostAnalytics = async ({ eventType, data }: PostAnalyticsType) => {
  let response: EventType | CartItemsEvent;
  switch (eventType) {
    case analyticEventTypes.ACTIONS:
      response = handleContentActionsEvent(data);
      break;

    case analyticEventTypes.DETAILS:
      response = handleContentDetailsEvent(data);
      break;

    case analyticEventTypes.CUSTOM:
      response = handleCustomEvent(data);
      break;

    default:
      console.warn("invalid event type.");
      return;
  }

  handleAnalyticEvent(response);
};
